'use client'
import React, { useEffect, useState } from 'react';
import '@/app/globals.css'
import useSWR from 'swr';
import NavBar from '@/app/components/navbar';
import { cachedFetchSites } from '@/app/utils/utils';
import { Site, InverterAction } from './types/global';
import { useUser } from '@/context/UserContext';
import { ArrowRight } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { parseAction, shortLabel } from '@/app/utils/interpretAction';
import OnboardingAssistant from '@/app/components/OnboardingAssistant';

// Brand accent — used only on the live pulse so the "Powston is running"
// signal carries the eye. Everything else uses the existing token system
// so this page stays consistent with the rest of the app.
const BRAND_ACCENT = '#60B6F4';

// Defensive UTC parser — API times usually arrive as a plain ISO string
// with no zone suffix ("2026-05-19T03:21:00"), but we've seen them come
// through already zoned, as a long JS Date toString, or as null in
// degenerate cases. Falling back across the candidate timestamps and
// validating the result avoids "Invalid time value" downstream.
function toUtcDate(time: unknown): Date | null {
  if (!time) return null;
  const s = String(time).trim();
  if (!s) return null;
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasZone ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pickActionTime(a: InverterAction): Date | null {
  return toUtcDate(a.interval_time) ?? toUtcDate(a.time) ?? toUtcDate(a.start_time);
}

// Pulse dot — small, brand-accent, indicates a live connection. Visible
// proof that the data below is current, not cached marketing copy.
function PulseDot() {
  return (
    <span className="relative inline-flex h-2 w-2" aria-hidden="true">
      <span
        className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
        style={{ backgroundColor: BRAND_ACCENT }}
      />
      <span
        className="relative inline-flex h-2 w-2 rounded-full"
        style={{ backgroundColor: BRAND_ACCENT }}
      />
    </span>
  );
}

// Live status row — fetches the latest action for each site's primary
// inverter, picks the freshest, and renders "Powston managed Xm ago —
// {action}". This is the page's one real proof point: a timestamp that
// changes throughout the day, not a marketing claim. Refreshes every
// 30 s so leaving the tab open keeps the signal honest.
function LiveStatus({ sites }: { sites: Site[] }) {
  const inverterIds = sites
    .map(s => s.primary_inverter_id)
    .filter((id): id is number => typeof id === 'number' && id > 0);

  const { data } = useSWR<InverterAction[]>(
    inverterIds.length ? ['home-live-actions', inverterIds.join(',')] : null,
    () => Promise.all(
      inverterIds.map(id =>
        fetch(`/api/inverter_action/${id}`).then(r => r.ok ? r.json() : null),
      ),
    ),
    { refreshInterval: 30000, revalidateOnFocus: true },
  );

  if (!data) return null;

  const freshest = data
    .map(a => {
      if (!a) return null;
      const at = pickActionTime(a);
      return at ? { at, action: a.action } : null;
    })
    .filter((x): x is { at: Date; action: string } => x !== null)
    .sort((a, b) => b.at.getTime() - a.at.getTime())[0];

  if (!freshest) return null;

  return (
    <div className="flex items-center gap-3 text-sm">
      <PulseDot />
      <span className="text-foreground">
        Powston managed {formatDistanceToNowStrict(freshest.at, { addSuffix: true })}
      </span>
      <span className="text-muted-foreground">— {freshest.action ? shortLabel(parseAction(freshest.action).battery) : ''}</span>
    </div>
  );
}

export default function HomePage() {
  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const { user, isHacker } = useUser();

  useEffect(() => {
    cachedFetchSites()
      .then(async (res) => {
        const data = await res.clone().json() as Site[];
        const sorted = data.sort((a, b) => (a.display_name || a.name).localeCompare(b.display_name || b.name));
        setSites(sorted);
        setSitesLoading(false);
        // Single site — owners go straight to their site detail. No
        // intermediate dashboard for the case where there's only one
        // thing to look at.
        if (sorted.length === 1) {
          window.location.href = `/summary/${sorted[0].id}`;
        }
      })
      .catch(err => {
        console.error('Error fetching sites:', err);
        setSitesLoading(false);
      });
  }, []);

  const hasSites = sites.length > 0;

  // Onboarding stages for users without a site yet (signup wizard
  // didn't run, design approved, controller shipping). Staff bypass —
  // they don't go through the customer funnel.
  type Stage = 1 | 2 | 3;
  let stage: Stage | null = null;
  if (!sitesLoading && !isHacker && !hasSites) {
    if (user?.shipping_tracking_url) stage = 3;
    else if (user?.signup_meta?.decision === 'proceed') stage = 2;
    else stage = 1;
  }

  return (
    <>
      <NavBar />
      <main className="flex min-h-screen flex-col bg-background text-foreground">
        <div className="max-w-3xl mx-auto w-full px-6 py-10 space-y-8">

          {/* ── Active customer: live proof + one-click to site ─────────── */}
          {!sitesLoading && hasSites && (
            <>
              <LiveStatus sites={sites} />

              <div>
                <h1 className="text-2xl font-semibold text-foreground">
                  Your sites
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Powston. Private Power.
                </p>
              </div>

              <div className="grid gap-2">
                {sites.map(site => (
                  <a
                    key={site.id}
                    href={`/summary/${site.id}`}
                    className="group flex items-center justify-between p-4 bg-card border border-border rounded-sm hover:border-foreground/30 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-base font-medium text-foreground truncate">
                        {site.display_name || site.name}
                      </p>
                      {(site.state || site.installed_battery) && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {[site.state, site.installed_battery ? `${Math.round(site.installed_battery / 1000)} kWh battery` : null]
                            .filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </a>
                ))}
              </div>

              {/* Supportive framing — lives below the action, never above
                  it. Plain English, asset-owner framing, no profit claim
                  or marketing cadence ("every five minutes"). */}
              <div className="pt-4 border-t border-border space-y-3 text-sm text-muted-foreground max-w-xl">
                <p>Your battery is being managed as an energy asset.</p>
                <p>
                  Powston monitors system readiness, coordinates trading behaviour, and
                  verifies performance so your solar, battery, and inverter can respond
                  to real energy-market conditions.
                </p>
              </div>
            </>
          )}

          {/* ── Staff with no assigned sites ────────────────────────────── */}
          {!sitesLoading && !hasSites && isHacker && (
            <div className="space-y-4">
              <h1 className="text-2xl font-semibold text-foreground">Powston</h1>
              <div className="bg-card border border-border rounded-sm p-6 max-w-lg">
                <p className="text-sm font-medium text-foreground mb-1">No sites in your view yet</p>
                <p className="text-sm text-muted-foreground">
                  Head to <a href="/sites" className="underline">Sites</a> to load the portfolio, or contact an admin if you expected sites here.
                </p>
              </div>
            </div>
          )}

          {/* ── Customer without a site yet — minimal stage-aware CTA ───── */}
          {!sitesLoading && !hasSites && !isHacker && (
            <div className="space-y-4 max-w-lg">
              <h1 className="text-2xl font-semibold text-foreground">Powston</h1>
              {stage === 3 && user?.shipping_tracking_url && (
                <>
                  <p className="text-muted-foreground">
                    Your controller is on its way. You'll see live status here once it's installed and connected.
                  </p>
                  <a
                    href={user.shipping_tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    Track your controller shipment
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </>
              )}
              {stage === 2 && (
                <>
                  <p className="text-muted-foreground">
                    Your design is approved. Order your controller to bring the system online.
                  </p>
                  <a href="/profile#subscription" className="btn-primary">
                    Order your controller
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </>
              )}
              {stage === 1 && (
                <>
                  <p className="text-muted-foreground">
                    Start with the Powston Energy Survey so we can design the right system for your site.
                  </p>
                  <a href="/signup" className="btn-primary">
                    Start the survey
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </>
              )}

              {/* Prominent onboarding assistant — the obvious next step. Helps the
                  user prepare, connect their brand's cloud, and get their network
                  controller-ready while the stage CTA above moves them through the
                  funnel. Pre-seeded from their survey answers. */}
              <div className="pt-2">
                <OnboardingAssistant signupMeta={user?.signup_meta} userName={user?.name} />
              </div>
            </div>
          )}

        </div>
      </main>
    </>
  );
}
